import { BaseProvider } from "./base.provider";

export interface Vehicle {
  vehicle_id: string;
  user_id: string;
  username: string;
  make: string;
  model: string;
  year: number;
  vehicle_type: "car" | "motorcycle";
  nickname: string | null;
  mod_list: string | null;
  photo_url: string | null;
  horsepower: number | null;
  drive: "FWD" | "RWD" | "AWD" | null;
  transmission: "A" | "M" | null;
  engine_position: "front" | "center" | "rear" | null;
  created_at: string;
  updated_at: string;
}

export interface CreateVehiclePayload {
  make: string;
  model: string;
  year: number;
  vehicle_type?: "car" | "motorcycle";
  nickname?: string;
  mod_list?: string;
  horsepower?: number;
  drive?: "FWD" | "RWD" | "AWD";
  transmission?: "A" | "M";
  engine_position?: "front" | "center" | "rear";
}

export interface PhotoUploadResponse {
  upload_url: string;
  form_fields: Record<string, string>;
}

export interface UpdateVehiclePayload {
  make: string;
  model: string;
  year: number;
  vehicle_type?: "car" | "motorcycle";
  nickname?: string | null;
  mod_list?: string | null;
  horsepower?: number | null;
  drive?: "FWD" | "RWD" | "AWD" | null;
  transmission?: "A" | "M" | null;
  engine_position?: "front" | "center" | "rear" | null;
}

class VehiclesProvider extends BaseProvider {
  create(payload: CreateVehiclePayload): Promise<{ id: string }> {
    return this.request<{ id: string }>("/v1/vehicles", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  getPhotoUploadUrl(vehicleId: string): Promise<PhotoUploadResponse> {
    return this.request<PhotoUploadResponse>(
      `/v1/vehicles/${encodeURIComponent(vehicleId)}/photo`,
      { method: "POST" }
    );
  }

  async uploadPhotoToStorage(
    { upload_url, form_fields }: PhotoUploadResponse,
    file: File
  ): Promise<void> {
    const form = new FormData();
    for (const [key, value] of Object.entries(form_fields)) {
      form.append(key, value);
    }
    form.append("Content-Type", file.type);
    form.append("file", file);
    const res = await fetch(upload_url, { method: "POST", body: form });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Storage upload failed: HTTP ${res.status}`);
    }
  }

  getByUser(userId: string): Promise<Vehicle[]> {
    return this.request<Vehicle[]>(
      `/v1/vehicles/user/${encodeURIComponent(userId)}`
    );
  }

  getById(vehicleId: string): Promise<Vehicle> {
    return this.request<Vehicle>(
      `/v1/vehicles/${encodeURIComponent(vehicleId)}`
    );
  }

  update(vehicleId: string, payload: UpdateVehiclePayload): Promise<string> {
    return this.request<string>(`/v1/vehicles/${encodeURIComponent(vehicleId)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  delete(vehicleId: string): Promise<string> {
    return this.request<string>(
      `/v1/vehicles/${encodeURIComponent(vehicleId)}`,
      { method: "DELETE" }
    );
  }
}

export const vehiclesProvider = new VehiclesProvider();
